import { z } from 'zod';
import { Nullable } from 'global';
import { SelectOption } from 'global';
import { InventoryOptionMatch } from 'modules/inventoryOptions';
import { ChargePriceInput, QuoteDepositType } from '@trnsact/trnsact-shared-types';
import { UseFormReturn } from 'react-hook-form/dist/types/form';
import type { Attachment } from 'modules/equipment';

import { dealBuilderFormSchema, tradeInListingItemSchema } from './model/schema';
import { ReactNode } from 'react';
import { ApolloError } from '@apollo/client';
import { DealBuilderSteps } from './ui/DealBuilderSteps';

export enum DealBuilderStep {
  Contacts = 'contacts',
  Equipment = 'equipment',
  Pricing = 'pricing',
  FinanceMenu = 'financeMenu',
  ReviewSend = 'reviewSend',
}

export interface StepConfig {
  step: DealBuilderStep;
  label: string;
  subLabel?: string;
}

export enum DealSummaryTab {
  Deal = 'deal',
  Equipment = 'equipment',
}

export interface DealBuilderSummary {
  hardCost: number;
  feesAndTaxes: number;
  customerDeposit: number;
  tradeIn: number;
  balanceDue: number;
}

export interface CalculateDealBuilderSummaryInput {
  hardCost: number;
  feesAndTaxes: number;
  depositValue: Nullable<number>;
  depositType: Nullable<QuoteDepositType>;
  tradeIn: number;
}

export interface QuoteEquipmentUpdateEntry {
  vin: string;
  cost: number;
  externalId: string;
  equipmentId: string;
  stockNumber: string;
  attributes: unknown[];
  price: ChargePriceInput;
}

export type DealBuilderSummaryItemColor = 'default' | 'secondary' | 'warning' | 'info';

export interface DealBuilderSummaryItemConfig {
  key: keyof DealBuilderSummary;
  label: string;
  color: DealBuilderSummaryItemColor;
}

export type DealBuilderFormValues = z.infer<typeof dealBuilderFormSchema>;

export interface DealBuilderCreditApplicationSource {
  id?: Nullable<string>;
}

export interface DealBuilderEquipmentSource {
  equipmentId: string;
  year: Nullable<string>;
  make: Nullable<string>;
  model: Nullable<string>;
  stockId: Nullable<string>;
  newUsed: Nullable<string>;
  mileage: Nullable<number>;
  retailPrice: Nullable<number>;
  tradeInValue: Nullable<number>;
  wholesaleCost: Nullable<number>;
  options: InventoryOptionMatch[];
  attachments: Attachment[];
  serialNumberVin: Nullable<string>;
  amountOwedOnTradeIn: Nullable<number>;
  assets: { id: string; source: string }[];
  additionalDescription: Nullable<string>;
  inventoryItem: Nullable<{ inventoryId: Nullable<string> }>;
}

// Normalized view-model used by the Contacts step
export interface DealBuilderAccount {
  name: Nullable<string>;
  address: Nullable<string>;
}

export interface DealBuilderContact {
  ownerPgId: Nullable<string>;
  accountContactId: Nullable<string>;
  fullName: string;
  title: Nullable<string>;
  email: Nullable<string>;
  phone: Nullable<string>;
}

export interface DealBuilderContactsData {
  account: Nullable<DealBuilderAccount>;
  primaryContact: Nullable<DealBuilderContact>;
  additionalRecipients: DealBuilderContact[];
  allContacts: DealBuilderContact[];
}

export interface SelectedContactsSummary {
  mainContact: Nullable<DealBuilderContact>;
  additionalCount: number;
  avatarIndex: number;
  selectedContacts: DealBuilderContact[];
}

export enum SendVia {
  Email = 'Email',
  Text = 'Text',
  TextAndEmail = 'TextAndEmail',
}

export interface SendEmailOption extends SelectOption<string> {
  fullName: string;
  email: string;
  title: string;
  group: EmailOptionGroups;
}

export enum EmailOptionGroups {
  CustomerContacts = 'Customer Contacts',
  DealershipContacts = 'Dealership Contacts',
  OtherContacts = 'Other Contacts',
}

export type TradeInListingItem = z.infer<typeof tradeInListingItemSchema>;
export type TradeInListingConfig = {
  form: UseFormReturn<DealBuilderFormValues>;
  onRemoveRow: (rowIndex: number) => void;
};

export type DealSummaryCardTypes =
  | 'equipmentTotal'
  | 'deposit'
  | 'balanceDue'
  | 'estimatedPayment'
  | 'customerSavings'
  | 'grossProfit'
  | 'profitMargin';

export interface DealBuilderIds {
  voId: string;
  quoteId: string;
  proposalId: string;
  dynamicsVOId: string;
}

export interface SaveAction {
  isLoading: boolean;
  isDisabled: boolean;
  info?: ReactNode;
  error: ApolloError | undefined;
  fn: (dealBuilderIds: DealBuilderIds) => Promise<void>;
}

export interface StepComponentProps {
  navigationNode: (action?: SaveAction) => ReactNode;
}
